import { ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getHealthGuardSnapshot } from '@/api/surveillance';
import { useHealthGuardState } from '@/hooks/useHealthGuardState';
import type { HealthGuardSnapshot } from '@/types/surveillance';
import { useUiConfig } from '@/hooks/useUiConfig';
import { healthGuardReasonVocabulary } from '@/presentation/vocabulary/healthGuard';
import HealthGuardDetailsModal from './HealthGuardDetailsModal';

const MS_PER_SECOND = 1000;

export default function HealthGuardNavIndicator() {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { config: uiConfig } = useUiConfig();
  const guardTiming = uiConfig.health_guard;
  const pollIntervalMs = guardTiming.poll_interval_seconds * MS_PER_SECOND;
  const staleMs = guardTiming.stale_seconds * MS_PER_SECOND;
  const { data } = useQuery({
    queryKey: ['health-guard-snapshot'],
    queryFn: () => getHealthGuardSnapshot(),
    refetchInterval: pollIntervalMs,
    staleTime: staleMs,
  });

  const snapshot = (data as HealthGuardSnapshot | null | undefined) ?? null;
  const { guardState, guardReason, guardQueuedText } = useHealthGuardState(snapshot);
  const reasonTerm = healthGuardReasonVocabulary.describe(guardReason);

  if (guardState !== 'degraded') {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setDetailsOpen(true)}
        className="so-focus-ring inline-flex min-h-control-comfortable shrink-0 items-center gap-2 rounded-control border border-status-blocked-border bg-status-blocked-surface px-3 text-control font-semibold text-status-blocked"
        title={
          reasonTerm.code
            ? `Some system checks need attention (${reasonTerm.text}). View details.`
            : 'Some system checks need attention. View details.'
        }
      >
        <ShieldAlert className="h-4 w-4" aria-hidden="true" />
        <span className="sr-only sm:not-sr-only">
          Checks need attention
          {guardQueuedText ? ` · ${guardQueuedText}` : ''}
        </span>
      </button>
      <HealthGuardDetailsModal
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        snapshot={snapshot}
      />
    </>
  );
}
